import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Check, Copy } from 'lucide-react';
import { richCopyText, richLength, type CoachGuide, type GuideImage, type RichText as RichTextValue } from '@/lib/coach-guide';

// A faithful copy of the creator web portal's /help page
// (creator-app-web/src/app/help/page.tsx + portal.css: .portal-intro,
// .portal-panel, .eyebrow, .prompt-row, .prompt-quote). Values are copied, not
// imported — the portal is a separate app. Update both together.

export type PreviewDevice = 'desktop' | 'phone';

// Widths of the portal's content area: desktop beside the 236px sidebar on a
// ~1340px window, phone at an iPhone's 390px.
const FRAME_WIDTH: Record<PreviewDevice, number> = { desktop: 1100, phone: 390 };

const FONT = "'Geist', 'Inter', -apple-system, BlinkMacSystemFont, 'SF Pro Text', system-ui, sans-serif";

/** Renders `children` at `width` px, scaled down to fit the available space. */
function ScaledFrame({ width, children }: { width: number; children: ReactNode }) {
  const outerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [height, setHeight] = useState(0);

  useLayoutEffect(() => {
    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return;
    const sync = () => {
      const s = Math.min(1, outer.clientWidth / width);
      setScale(s);
      // offsetHeight ignores the transform, so this is the unscaled height.
      setHeight(inner.offsetHeight * s);
    };
    sync();
    const observer = new ResizeObserver(sync);
    observer.observe(outer);
    observer.observe(inner);
    return () => observer.disconnect();
  }, [width]);

  return (
    <div ref={outerRef} className="w-full overflow-hidden" style={{ height }}>
      <div
        ref={innerRef}
        style={{ width, transform: `scale(${scale})`, transformOrigin: 'top left', marginInline: scale === 1 ? 'auto' : undefined }}
      >
        {children}
      </div>
    </div>
  );
}

/** Rich text drawn like the portal's GuideRichText: bold, italic, sized and linked runs. */
function RichText({ text, linkColor }: { text: RichTextValue; linkColor: string }) {
  return (
    <>
      {text.map((run, i) => {
        let node: ReactNode = run.text;
        if (run.bold) node = <strong>{node}</strong>;
        if (run.italic) node = <em>{node}</em>;
        if (run.size) node = <span style={{ fontSize: run.size }}>{node}</span>;
        if (run.href) {
          node = (
            <a
              href={run.href}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: linkColor, textDecoration: 'underline', textUnderlineOffset: 2, overflowWrap: 'anywhere' }}
            >
              {node}
            </a>
          );
        }
        return <span key={i}>{node}</span>;
      })}
    </>
  );
}

const IMAGE_WIDTH = { full: '100%', medium: '60%', small: '35%' } as const;

export function CoachGuidePreview({
  guide,
  device,
  highlightId,
}: {
  guide: CoachGuide;
  device: PreviewDevice;
  /** Section being edited, outlined so the admin can find it. */
  highlightId?: string | null;
}) {
  const [copied, setCopied] = useState('');
  const phone = device === 'phone';
  const t = guide.theme;
  const ty = guide.typography;

  const copy = async (id: string, text: string) => {
    try { await navigator.clipboard.writeText(text); } catch { /* preview only */ }
    setCopied(id);
    setTimeout(() => setCopied(''), 1500);
  };

  const panel: CSSProperties = {
    background: t.panelBackground,
    border: `1px solid ${t.panelBorder}`,
    borderRadius: 18,
    padding: phone ? 20 : 26,
    transition: 'box-shadow 0.2s',
  };
  const eyebrow: CSSProperties = {
    fontSize: ty.eyebrowSize, letterSpacing: '0.13em', color: t.eyebrow, fontWeight: 600, margin: '0 0 12px',
  };
  const bodyText: CSSProperties = { color: t.bodyText, fontSize: ty.bodySize, lineHeight: 1.6, whiteSpace: 'pre-line', margin: 0 };

  const image = (img: GuideImage) => (
    <img
      src={img.url}
      alt={img.alt}
      style={{
        display: 'block',
        width: phone ? '100%' : IMAGE_WIDTH[img.width],
        maxWidth: '100%',
        height: 'auto',
        borderRadius: 12,
        border: `1px solid ${t.panelBorder}`,
        margin: img.position === 'top' ? '2px 0 16px' : '16px 0 0',
      }}
    />
  );

  return (
    <ScaledFrame width={FRAME_WIDTH[device]}>
      <div
        style={{
          background: '#050507',
          color: '#ffffff',
          fontFamily: FONT,
          padding: phone ? '26px 20px 40px' : '46px 48px 56px',
          textAlign: 'left',
          minHeight: 200,
        }}
      >
        <header style={{ marginBottom: phone ? 26 : 32 }}>
          {/* The portal scales title/subtitle down on small screens (38 → 32, 15 → 14). */}
          <h1 style={{ fontSize: phone ? ty.titleSize * 0.842 : ty.titleSize, letterSpacing: '-0.045em', lineHeight: 1.15, fontWeight: 650, color: t.pageTitle, margin: 0 }}>
            {richLength(guide.title) > 0 ? <RichText text={guide.title} linkColor={t.link} /> : <span style={{ opacity: 0.35 }}>Page title</span>}
          </h1>
          {richLength(guide.subtitle) > 0 && (
            <p style={{ color: t.pageSubtitle, whiteSpace: 'pre-line', fontSize: phone ? ty.subtitleSize * 0.933 : ty.subtitleSize, lineHeight: 1.6, marginTop: 12, maxWidth: 660 }}>
              <RichText text={guide.subtitle} linkColor={t.link} />
            </p>
          )}
        </header>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          {guide.sections.map((s) => {
            const accent = s.accent || t.accent;
            const highlighted = highlightId === s.id;
            const items = s.items.filter((p) => richLength(p.title) || richLength(p.text));
            return (
              <section
                key={s.id}
                style={{ ...panel, boxShadow: highlighted ? `0 0 0 2px ${accent}` : undefined }}
              >
                {richLength(s.eyebrow) > 0 && (
                  <p style={eyebrow}><span><RichText text={s.eyebrow} linkColor={t.link} /></span></p>
                )}
                {s.image?.position === 'top' && image(s.image)}
                {s.kind === 'text' ? (
                  richLength(s.body) > 0
                    ? <p style={bodyText}><RichText text={s.body} linkColor={t.link} /></p>
                    : !s.image && <p style={{ ...bodyText, opacity: 0.4 }}>Section text…</p>
                ) : (
                  <>
                    {richLength(s.body) > 0 && <p style={{ ...bodyText, marginBottom: 10 }}><RichText text={s.body} linkColor={t.link} /></p>}
                    {items.length === 0 && <p style={{ ...bodyText, opacity: 0.4 }}>No prompts yet</p>}
                    {items.map((p, i) => (
                      <div
                        key={p.id}
                        style={{
                          display: 'flex',
                          flexDirection: phone ? 'column' : 'row',
                          alignItems: 'flex-start',
                          justifyContent: 'space-between',
                          gap: 16,
                          padding: i === 0 ? '4px 0 14px' : '14px 0',
                          borderTop: i === 0 ? 0 : `1px solid ${t.panelBorder}`,
                        }}
                      >
                        <div style={{ minWidth: 0 }}>
                          <strong style={{ fontSize: ty.promptTitleSize, color: t.promptTitle, fontWeight: 700 }}>{richLength(p.title) ? <RichText text={p.title} linkColor={t.link} /> : 'Untitled prompt'}</strong>
                          <blockquote
                            style={{
                              margin: '6px 0 0',
                              padding: '10px 12px',
                              borderLeft: `2px solid ${accent}`,
                              background: t.quoteBackground,
                              borderRadius: '0 10px 10px 0',
                              fontSize: ty.promptTextSize,
                              lineHeight: 1.55,
                              color: t.quoteText,
                              whiteSpace: 'pre-wrap',
                              overflowWrap: 'anywhere',
                            }}
                          >
                            <RichText text={p.text} linkColor={t.link} />
                          </blockquote>
                        </div>
                        <button
                          type="button"
                          onClick={() => copy(p.id, richCopyText(p.text))}
                          style={{
                            flex: 'none',
                            marginTop: 2,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 9,
                            borderRadius: 999,
                            minHeight: 42,
                            padding: '11px 19px',
                            background: 'transparent',
                            color: t.buttonText,
                            border: `1px solid ${t.panelBorder}`,
                            fontSize: ty.buttonSize,
                            fontWeight: 600,
                            whiteSpace: 'nowrap',
                            cursor: 'pointer',
                            fontFamily: FONT,
                          }}
                        >
                          {copied === p.id ? <><Check size={14} /> Copied</> : <><Copy size={14} /> Copy</>}
                        </button>
                      </div>
                    ))}
                  </>
                )}
                {s.image?.position === 'bottom' && image(s.image)}
              </section>
            );
          })}
        </div>
      </div>
    </ScaledFrame>
  );
}
